/**
 * LiveStreamingVideoView — the full-screen live video surface.
 *
 * Web port of the Flutter `videoView` / `localView` / `remoteView` widgets
 * (`lib/presentation/screens/community/live_streaming_screen.dart:1228-1271`),
 * mapped onto `agora-rtc-react`'s declarative video components (the
 * `VideoCallVideoViews` idiom, simplified):
 *   - the broadcaster sees ITS OWN camera track full-screen via
 *     `<LocalVideoTrack>` (Flutter `localView` `VideoCanvas(uid: 0)`, `:1241-1246`);
 *   - the audience sees the BROADCASTER'S remote stream full-screen via
 *     `<RemoteUser>`, selected by the broadcaster's resolved streaming uid
 *     (Flutter `remoteView` `VideoCanvas(uid: broadcasterStreamingId)`, `:1260-1267`).
 *
 * Unlike the 1:1 video call there is exactly ONE full-screen view per role — no
 * PiP self-view and no view-swap (live streaming is one-directional). The whole
 * surface is a tap-to-toggle clear-screen affordance (Flutter's tap
 * `GestureDetector` toggling `_clearScreen`, `:1234-1240` / `:1253-1259`): the
 * page reads `clearScreen` to hide/show its controls + overlays. Rendered as a
 * `role="button"` toggle with `aria-pressed={clearScreen}` and keyboard
 * activation via `useEnterOrSpaceActivation` (the `CommunityMessageItem` a11y
 * idiom).
 *
 * The pre-first-frame / opening-channel loading gate (Flutter `loadingWidget`,
 * `:1248`/`:1270`) is NOT this component's concern — the page (Task 14) gates
 * this surface behind `isOpeningChannel` and renders the loading overlay (Task
 * 13). This component only renders the video surface itself.
 *
 * Theme/responsive/`qaAttr` are mandatory (`presentation.md`). The surface
 * carries no user-visible copy, so l10n is consumed as the localized
 * accessibility `aria-label`.
 */

import React, { useMemo } from 'react';
import { RemoteUser, LocalVideoTrack } from 'agora-rtc-react';
import type { IAgoraRTCRemoteUser, ICameraVideoTrack } from 'agora-rtc-react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useEnterOrSpaceActivation } from '@/presentation/hooks/useEnterOrSpaceActivation';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface LiveStreamingVideoViewProps {
  /** `_isBroadcaster` — selects local self-view vs. broadcaster remote view (`:1229`). */
  isBroadcaster: boolean;
  /** The broadcaster's local camera track (Flutter `VideoCanvas(uid: 0)`, `:1244`). Null for the audience. */
  localCameraTrack: ICameraVideoTrack | null;
  /** The remote users currently in the channel (the audience selects the broadcaster by uid). */
  remoteUsers: IAgoraRTCRemoteUser[];
  /**
   * Audience-only: the broadcaster's resolved streaming uid — the remote uid the
   * view matches the broadcaster's stream on (Flutter `broadcasterStreamingId`,
   * `:1263`). Undefined on the broadcaster side.
   */
  broadcasterStreamingId?: number;
  /** `_clearScreen` — reflected as the toggle button's `aria-pressed` state (`:1236`/`:1255`). */
  clearScreen: boolean;
  /** Toggle `clearScreen` (Flutter tap `GestureDetector`, `:1234-1240` / `:1253-1259`). */
  onToggleClearScreen: () => void;
}

export const LiveStreamingVideoView: React.FC<LiveStreamingVideoViewProps> = ({
  isBroadcaster,
  localCameraTrack,
  remoteUsers,
  broadcasterStreamingId,
  clearScreen,
  onToggleClearScreen,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const handleKeyDown =
    useEnterOrSpaceActivation<HTMLDivElement>(onToggleClearScreen);

  // Audience: pick the broadcaster's remote user out of the channel members —
  // match on the resolved `broadcasterStreamingId` when present (Flutter
  // `VideoCanvas(uid: broadcasterStreamingId)`, `:1263`), else fall back to the
  // first remote user. `uid` is a `UID = string | number`, so compare loosely on
  // the numeric value.
  const broadcasterRemoteUser = useMemo(() => {
    if (isBroadcaster || remoteUsers.length === 0) {
      return undefined;
    }
    if (broadcasterStreamingId != null) {
      return (
        remoteUsers.find(
          (user) => Number(user.uid) === Number(broadcasterStreamingId),
        ) ?? remoteUsers[0]
      );
    }
    return remoteUsers[0];
  }, [isBroadcaster, remoteUsers, broadcasterStreamingId]);

  // Broadcaster self-view — Flutter `localView` `AgoraVideoView`
  // `VideoCanvas(uid: 0)` (`:1241-1246`). `<LocalVideoTrack play>` mounts the
  // camera track into its managed container. Memoized on `[localCameraTrack]`
  // so unrelated re-renders (e.g. a watcher-count tick) don't remount the Agora
  // video DOM (the `VideoCallVideoViews` flicker-avoidance idiom).
  const broadcasterStream = useMemo(
    () => (
      <LocalVideoTrack
        track={localCameraTrack}
        play
        videoPlayerConfig={{ fit: 'contain' }}
        style={{ width: '100%', height: '100%' }}
      />
    ),
    [localCameraTrack],
  );

  // Audience broadcaster view — Flutter `remoteView` `AgoraVideoView.remote`
  // (`:1260-1267`). `<RemoteUser>` mounts/plays the broadcaster's video + audio
  // into a full-bleed container. Memoized on `[broadcasterRemoteUser]`.
  const audienceStream = useMemo(
    () =>
      broadcasterRemoteUser && (
        <RemoteUser
          user={broadcasterRemoteUser}
          playVideo
          playAudio
          videoPlayerConfig={{ fit: 'contain' }}
          style={{ width: '100%', height: '100%' }}
        />
      ),
    [broadcasterRemoteUser],
  );

  return (
    // Full-viewport tap surface — a tap toggles clear-screen (Flutter
    // `GestureDetector` `onTap` toggling `_clearScreen`, `:1234-1240` /
    // `:1253-1259`). `role="button"` + `aria-pressed` expose it as the
    // clear-screen toggle; `useEnterOrSpaceActivation` restores keyboard
    // activation.
    <div
      {...qaAttr({
        id: 'live-streaming-video',
        status: isBroadcaster ? 'broadcaster' : 'audience',
      })}
      role="button"
      tabIndex={0}
      aria-pressed={clearScreen}
      aria-label={l10n.community.liveStreamingPage.videoLabel}
      onClick={onToggleClearScreen}
      onKeyDown={handleKeyDown}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100dvh',
        backgroundColor: theme.background,
        cursor: 'pointer',
        overflow: 'hidden',
        // Contain the Agora player's internal stacking context. Without this the
        // player's positively z-indexed inner elements escape to the ROOT
        // stacking context (this div has `z-index: auto`) and paint above the
        // later-sibling chat overlay — so audience taps meant for the composer /
        // message list land on this video subtree and toggle clear-screen.
        // `isolation: 'isolate'` forces a new stacking context here so nothing
        // inside can escape, without changing this div's own auto-z relationship
        // with later siblings (the overlay + controls still stack over it).
        isolation: 'isolate',
      }}
    >
      {isBroadcaster ? broadcasterStream : audienceStream}
    </div>
  );
};
